import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pt-BR");
};

const formatStatus = (status) => {
  if (!status) return "—";
  const key = `dashboard.ticketsByContactTag.status.${status}`;
  const label = i18n.t(key);
  return label === key ? status : label;
};

// Lista os chamados abertos no período de uma tag do contato (ou dos contatos
// sem tag, quando `tag.untagged`). Endpoint restrito a administradores.
const ContactTagTicketsModal = ({ open, onClose, tag, initialDate, finalDate }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState([]);
  const [total, setTotal] = useState(0);

  const tagId = tag?.tagId;
  const untagged = !!tag?.untagged;

  useEffect(() => {
    if (!open || (!tagId && !untagged) || !initialDate || !finalDate) return;

    const controller = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/dashboard/ticketsByContactTag/details", {
          params: {
            initialDate,
            finalDate,
            ...(untagged ? { untagged: true } : { tagId }),
          },
          signal: controller.signal,
        });
        setDetails(Array.isArray(data?.details) ? data.details : []);
        setTotal(Number(data?.total) || 0);
      } catch (err) {
        if (controller.signal.aborted) return;
        setDetails([]);
        setTotal(0);
        toastError(err);
      }
      if (!controller.signal.aborted) setLoading(false);
    })();

    return () => controller.abort();
  }, [open, initialDate, finalDate, tagId, untagged]);

  const handleOpenTicket = (detail) => {
    if (!detail?.uuid) return;
    onClose();
    history.push(`/tickets/${detail.uuid}`);
  };

  const title = untagged
    ? i18n.t("dashboard.ticketsByContactTag.detailsTitleUntagged")
    : `${i18n.t("dashboard.ticketsByContactTag.detailsTitle")} — ${tag?.tagName || ""}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {loading && <CircularProgress size={24} />}

        {!loading && details.length === 0 && (
          <Typography>{i18n.t("dashboard.ticketsByContactTag.detailsEmpty")}</Typography>
        )}

        {!loading && details.length > 0 && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("dashboard.ticketsByContactTag.table.ticket")}</TableCell>
                  <TableCell>{i18n.t("dashboard.ticketsByContactTag.table.contact")}</TableCell>
                  <TableCell>{i18n.t("dashboard.ticketsByContactTag.table.queue")}</TableCell>
                  <TableCell>{i18n.t("dashboard.ticketsByContactTag.table.connection")}</TableCell>
                  <TableCell>{i18n.t("dashboard.ticketsByContactTag.table.user")}</TableCell>
                  <TableCell>{i18n.t("dashboard.ticketsByContactTag.table.status")}</TableCell>
                  <TableCell>{i18n.t("dashboard.ticketsByContactTag.table.openedAt")}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {details.map((detail) => (
                  <TableRow key={detail.ticketId}>
                    <TableCell>#{detail.ticketId}</TableCell>
                    <TableCell>
                      {detail.contactName || "—"}
                      {detail.contactNumber ? ` (${detail.contactNumber})` : ""}
                    </TableCell>
                    <TableCell>{detail.queueName || "—"}</TableCell>
                    <TableCell>{detail.whatsappName || "—"}</TableCell>
                    <TableCell>{detail.userName || "—"}</TableCell>
                    <TableCell>{formatStatus(detail.ticketStatus)}</TableCell>
                    <TableCell>{formatDateTime(detail.openedAt)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="primary"
                        disabled={!detail.uuid}
                        onClick={() => handleOpenTicket(detail)}
                      >
                        {i18n.t("dashboard.ticketsByContactTag.table.open")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {total > details.length && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                {i18n.t("dashboard.ticketsByContactTag.showingLimit", {
                  shown: details.length,
                  total,
                })}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          {i18n.t("dashboard.ticketsByContactTag.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactTagTicketsModal;
